/**
 * @file ClientTierDropdown.js
 * @description A dropdown component for selecting a client tier.
 */

import React, { useState, useEffect } from 'react';
import { fetchClientTiers } from '../../../services/systemService';
import { Dropdown } from '../../helpers/Dropdown';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import Toast from '../../ui/toasts/Toast';
import { getText } from '../../../i18n';

/**
 * A dropdown component for selecting a client tier.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines whether the label should be displayed inline.
 * @param {string} props.selectedClientTier - The ID of the selected client tier.
 * @param {Function} props.onClientTierChange - The callback function to be called when the client tier selection changes.
 * @param {string} [props.label="Client Tier"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines whether the dropdown is required.
 * @param {Array} [props.allowedTiers=[]] - An array of allowed client tier IDs to be displayed in the dropdown. If empty, all tiers will be shown.
 * @param {string} [props.getDefaultLabel="Select Client Tier"] - The default label text for the dropdown.
 * @returns {JSX.Element} The rendered ClientTierDropdown component.
 */
const ClientTierDropdown = ({
    className,
    inlineLabel = true,
    selectedClientTier,
    onClientTierChange,
    label = 'Client Tier',
    isRequired = false,
    allowedTiers = [],
    defaultLabel = 'Select Client Tier',
}) => {
    const [clientTiers, setClientTiers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const getClientTiers = async () => {
            setIsLoading(true);
            try {
                const data = await fetchClientTiers();
                setClientTiers(data);
            } catch (error) {
                console.error('Could not fetch client tiers:', error);
                Toast.error(getText('msg_436b8e9da603'));
            } finally {
                setIsLoading(false);
            }
        };

        getClientTiers();
    }, []);

    const getDefaultLabel = () => {
        const matchingClientTier = clientTiers.find(
            clientTier => clientTier.id === selectedClientTier,
        );
        return matchingClientTier ? matchingClientTier.name : defaultLabel;
    };

    const filteredTiers =
        allowedTiers.length > 0
            ? clientTiers.filter(tier => allowedTiers.includes(tier.id))
            : clientTiers;

    const clientTierOptions = filteredTiers.map(clientTier => ({
        value: clientTier.id,
        label: clientTier.name,
    }));

    return (
        <div
            className={`form-group mb-3 ${
                inlineLabel ? 'inline-input' : ''
            } ${className}`}
        >
            <label htmlFor="client-tier-dropdown">
                {label}
                {isRequired && <span className="text-danger">*</span>}
            </label>
            <LoadingWrapper isLoading={isLoading}>
                <Dropdown
                    id="client-tier-dropdown"
                    defaultValue={getDefaultLabel()}
                    options={clientTierOptions}
                    value={selectedClientTier}
                    onChange={onClientTierChange}
                    disabled={clientTiers.length === 0 || isLoading}
                />
            </LoadingWrapper>
        </div>
    );
};

export default ClientTierDropdown;
